import styled, { css, keyframes } from "styled-components";
import { getColor } from "~/helpers/style";

/* ------------------------------------------------------------------ */
/*  Sticky Banner (bottom-left dark card)                             */
/* ------------------------------------------------------------------ */

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

export const BannerWrapper = styled.div`
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 1000;
  animation: ${slideUp} 0.35s ease-out;
`;

export const BannerCard = styled.div`
  background: #2d2d3d;
  color: #ffffff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
`;

export const BannerTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
`;

export const BannerDescription = styled.p`
  font-size: 0.875rem;
  font-weight: 400;
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
`;

export const ClaimButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #2d2d3d;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const CloseButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  ${({ $visible }) =>
    $visible
      ? css`
          animation: ${fadeIn} 0.3s ease-in;
        `
      : css`
          visibility: hidden;
          pointer-events: none;
        `}

  &:hover {
    color: #ffffff;
  }
`;

/* ------------------------------------------------------------------ */
/*  Confirm Modal                                                     */
/* ------------------------------------------------------------------ */

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0.5rem 0;
`;

export const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: ${getColor("text")};
`;

export const ModalDescription = styled.p`
  font-size: 0.875rem;
  font-weight: 400;
  margin: 0;
  color: ${getColor("textSecondary")};
  line-height: 1.5;
`;

export const TestimonialRow = styled.div`
  background: ${getColor("primaryLight")};
  border-radius: 0.5rem;
  padding: 1rem;
  font-size: 0.8125rem;
  font-style: italic;
  color: ${getColor("text")};
  line-height: 1.5;
`;

export const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f8fa;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
`;

export const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

export const CardLabel = styled.span`
  font-size: 0.8125rem;
  color: ${getColor("textSecondary")};
`;

export const CardDigits = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${getColor("text")};
`;

export const ChangeCardLink = styled.a`
  font-size: 0.8125rem;
  color: ${getColor("primary")};
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

export const ConfirmButton = styled.button<{ $loading?: boolean }>`
  width: 100%;
  padding: 0.875rem 1.25rem;
  border: none;
  border-radius: 0.5rem;
  background: ${getColor("primary")};
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
